import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getUser, getToken, setUser as setLocalUser } from '../utils/auth';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CONFIG from '../config';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/common/SEO';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { maskEmail, maskUsername, maskFullName, maskPhone } from '../utils/anonymizer';

// Helper to submit objection for a hostname owner
const submitObjection = async (userId) => {
    const reason = window.prompt('Please provide a reason for your objection:');
    if (reason === null) return; // cancelled
    if (!reason.trim()) {
        alert('Reason is required.');
        return;
    }
    try {
        const token = getToken();
        await axios.post(`${CONFIG.API_URL}/api/admin/data-subject/objection`, { userId, reason }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        alert('Objection recorded successfully.');
        // Refresh user profile to update objection status
        // await fetchProfile();
    } catch (err) {
        console.error('Failed to record objection:', err);
        alert('Failed to record objection.');
    }
};

const UserProfile = () => {
    const { t, i18n } = useTranslation()
    const isArabic = i18n.language.startsWith('ar')
    const [user, setUser] = useState(getUser())
    const [isEditing, setIsEditing] = useState(false)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [formData, setFormData] = useState({ username: '', fullname: '', phone: '' })

    const { cartItems } = useCart()
    const { wishlistItems } = useWishlist()
    const [ordersCount, setOrdersCount] = useState(0)
    // Export filter state
    const [exportFilter, setExportFilter] = useState({
        startDate: '',
        endDate: '',
        includeOrders: false,
        includeConsents: false,
        includeAll: false,
    })
    const [showExportFilters, setShowExportFilters] = useState(false)

    const fetchProfile = async () => {
        try {
            setLoading(true)
            const token = getToken()
            const res = await axios.get(`${CONFIG.API_URL}/api/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            setUser(res.data)
            setLocalUser(res.data); // Keep localStorage in sync
            setFormData({
                username: res.data.username || '',
                fullname: res.data.fullname || '',
                phone: res.data.phone || ''
            })
        } catch (err) {
            console.error('Failed to fetch profile:', err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchProfile()
    }, [])

    useEffect(() => {
        const fetchOrdersCount = async () => {
            try {
                const token = getToken()
                const res = await axios.get(`${CONFIG.API_URL}/api/orders/my-orders`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
                setOrdersCount(res.data.length)
            } catch (err) {
                console.error('Failed to fetch orders count:', err)
            }
        }
        fetchOrdersCount()
    }, [])

    const handleSave = async () => {
        try {
            setSaving(true)
            const token = getToken()
            await axios.put(`${CONFIG.API_URL}/api/auth/profile`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            })
            await fetchProfile()
            setIsEditing(false)
            alert(t('profile.update_success', 'Profile updated successfully!'))
        } catch (err) {
            console.error('Failed to save profile:', err)
            alert(t('profile.update_error', 'Failed to update profile.'))
        } finally {
            setSaving(false)
        }
    }

    const stats = [
        {
            label: t('profile.total_orders', 'Orders'),
            value: ordersCount,
            icon: 'fa-box',
            link: '/profile/orders'
        },
        {
            label: t('profile.wishlist', 'Wishlist'),
            value: wishlistItems.length,
            icon: 'fa-heart',
            link: '/wishlist'
        },
        {
            label: t('profile.cart', 'Cart'),
            value: cartItems.length,
            icon: 'fa-shopping-cart',
            link: '/cart'
        }
    ]

    if (loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>

    return (
        <>
            <Helmet>
                <html lang={isArabic ? 'ar' : 'en'} dir={isArabic ? 'rtl' : 'ltr'} />
            </Helmet>
            <SEO />
            <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-20 animate-fade-in">
                <div className="flex flex-col lg:flex-row gap-12">
                    {/* Sidebar */}
                    <div className="w-full lg:w-80 space-y-4">
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl p-8 text-center overflow-hidden relative">
                            <div className="absolute top-0 left-0 w-full h-24 bg-brand-primary/5" />
                            <div className="relative mt-8">
                                <div className="w-24 h-24 mx-auto rounded-full bg-brand-primary text-white flex items-center justify-center text-4xl font-black shadow-xl shadow-brand-primary/20 mb-6 border-4 border-white">
                                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                                </div>
                                <h2 className="text-xl font-black text-gray-900 mb-1">{maskUsername(user?.username)}</h2>
                                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">{maskEmail(user?.email)}</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-4 space-y-1">
                            <Link to="/profile" className="flex items-center gap-4 p-4 rounded-2xl bg-brand-primary text-white shadow-lg shadow-brand-primary/10 transition-all font-black text-sm">
                                <i className="fas fa-user-circle w-6"></i>
                                {t('profile.personal_info', 'Personal Info')}
                            </Link>
                            <Link to="/profile/orders" className="flex items-center gap-4 p-4 rounded-2xl text-gray-400 hover:bg-gray-50 hover:text-gray-900 transition-all font-black text-sm">
                                <i className="fas fa-shopping-bag w-6"></i>
                                {t('profile.my_orders', 'My Orders')}
                            </Link>
                            <Link to="/wishlist" className="flex items-center gap-4 p-4 rounded-2xl text-gray-400 hover:bg-gray-50 hover:text-gray-900 transition-all font-black text-sm">
                                <i className="fas fa-heart w-6"></i>
                                {t('profile.wishlist_sidebar', 'Wishlist')}
                            </Link>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 space-y-8">
                        {/* Stats */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                            {stats.map((s, idx) => (
                                <Link key={idx} to={s.link} className="bg-white rounded-3xl border border-gray-100 shadow-lg p-6 hover:shadow-2xl hover:-translate-y-1 transition-all group">
                                    <div className="flex items-center gap-6">
                                        <div className="w-14 h-14 rounded-2xl bg-gray-50 text-gray-400 flex items-center justify-center text-xl group-hover:bg-brand-primary group-hover:text-white transition-all">
                                            <i className={`fas ${s.icon}`}></i>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">{s.label}</p>
                                            <p className="text-2xl font-black text-gray-900">{s.value}</p>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        {/* Content Section */}
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl p-10">
                            <div className="justify-between mb-10 pb-6 border-b border-gray-50">
                                <h3 className="text-2xl font-black text-gray-900 tracking-tight">{t('profile.account_settings', 'Account Settings')}</h3>
                                <div className="items-center gap-2 w-100 flex">
                                    <div className=''>
                                        <button
                                            onClick={() => setShowExportFilters(prev => !prev)}
                                            className="bg-gray-50 order-1 text-gray-400 hover:text-brand-primary hover:bg-brand-primary/5 px-3 py-1 rounded text-sm"
                                        >
                                            {showExportFilters ? t('profile.hide_filters', 'Hide Filters') : t('profile.show_filters', 'Show Filters')}
                                        </button>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const token = getToken();
                                                    // Build query params based on selected filters
                                                    const params = {};
                                                    if (exportFilter.startDate) params.start = exportFilter.startDate;
                                                    if (exportFilter.endDate) params.end = exportFilter.endDate;
                                                    if (exportFilter.includeOrders) params.orders = true;
                                                    if (exportFilter.includeConsents) params.consents = true;
                                                    if (exportFilter.includeAll) params.all = true;
                                                    const query = new URLSearchParams(params).toString();
                                                    const url = query ? `${CONFIG.API_URL}/api/users/export?${query}` : `${CONFIG.API_URL}/api/users/export`;
                                                    const res = await axios.get(url, {
                                                        headers: { Authorization: `Bearer ${token}` },
                                                        responseType: 'blob'
                                                    });
                                                    const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
                                                    const link = document.createElement('a');
                                                    link.href = downloadUrl;
                                                    link.setAttribute('download', 'personal_data.json');
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    link.remove();
                                                } catch (err) {
                                                    console.error('Export failed', err);
                                                    alert(t('profile.export_error', 'Failed to export data'));
                                                }
                                            }}
                                            className="bg-brand-primary text-white px-6 py-2.5 rounded-full font-black text-[10px] uppercase tracking-widest hover:bg-brand-primary/80 transition-all"
                                        >
                                            {t('profile.export_data', 'Export Data')}
                                        </button>
                                    </div>
                                    {showExportFilters && (
                                        <div className="flex items-center space-x-2 mb-2">
                                            <input
                                                type="date"
                                                value={exportFilter.startDate}
                                                onChange={e => setExportFilter({ ...exportFilter, startDate: e.target.value })}
                                                className="p-2 border rounded"
                                                placeholder={t('profile.start_date', 'Start Date')}
                                            />
                                            <input
                                                type="date"
                                                value={exportFilter.endDate}
                                                onChange={e => setExportFilter({ ...exportFilter, endDate: e.target.value })}
                                                className="p-2 border rounded"
                                                placeholder={t('profile.end_date', 'End Date')}
                                            />
                                            <label className="flex items-center space-x-1">
                                                <input
                                                    type="checkbox"
                                                    checked={exportFilter.includeOrders}
                                                    onChange={e => setExportFilter({ ...exportFilter, includeOrders: e.target.checked })}
                                                />
                                                <span className="text-sm">{t('profile.include_orders', 'Orders')}</span>
                                            </label>
                                            <label className="flex items-center space-x-1">
                                                <input
                                                    type="checkbox"
                                                    checked={exportFilter.includeConsents}
                                                    onChange={e => setExportFilter({ ...exportFilter, includeConsents: e.target.checked })}
                                                />
                                                <span className="text-sm">{t('profile.include_consents', 'Consents')}</span>
                                            </label>
                                            <label className="flex items-center space-x-1">
                                                <input
                                                    type="checkbox"
                                                    checked={exportFilter.includeAll}
                                                    onChange={e => setExportFilter({ ...exportFilter, includeAll: e.target.checked })}
                                                />
                                                <span className="text-sm">{t('profile.include_all', 'All Data')}</span>
                                            </label>
                                        </div>
                                    )}
                                    <div className="items-center space-x-2 mb-2">

                                        {/* Restrict Processing */}
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const token = getToken();
                                                    await axios.post(`${CONFIG.API_URL}/api/auth/restrict`, { userId: user.id }, {
                                                        headers: { Authorization: `Bearer ${token}` }
                                                    });
                                                    alert(t('profile.restrict_success', 'Processing restricted successfully.'));
                                                } catch (err) {
                                                    console.error('Failed to restrict processing:', err);
                                                    alert(t('profile.restrict_error', 'Failed to restrict processing.'));
                                                }
                                            }}
                                            className="bg-yellow-600 text-white px-4 py-2 rounded-full font-black text-[10px] uppercase tracking-widest hover:bg-yellow-700 transition-all"
                                        >
                                            {t('profile.restrict_processing', 'Restrict Processing')}
                                        </button>
                                        {/* Object to Processing */}
                                        <button
                                            onClick={() => submitObjection(user.id)}
                                            className="bg-red-600 text-white px-4 py-2 rounded-full font-black text-[10px] uppercase tracking-widest hover:bg-red-700 transition-all"
                                        >
                                            {t('profile.object_processing', 'Object to Processing')}
                                        </button>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsEditing(!isEditing)}
                                    className="bg-gray-50 text-gray-400 hover:text-brand-primary hover:bg-brand-primary/5 px-6 py-2.5 rounded-full font-black text-[10px] uppercase tracking-widest transition-all"
                                >
                                    {isEditing ? t('profile.cancel', 'Cancel') : t('profile.edit', 'Edit Profile')}
                                </button>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{t('profile.username', 'Username')}</label>
                                            <input
                                                type="text"
                                                readOnly={!isEditing}
                                                className={`w-full p-4 rounded-2xl text-sm font-medium transition-all ${isEditing ? 'bg-gray-50 border-none focus:ring-2 focus:ring-brand-primary/10' : 'bg-transparent border-transparent cursor-default'}`}
                                                value={formData && maskUsername(user?.username)}
                                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{t('profile.full_name', 'Full Name')}</label>
                                            <input
                                                type="text"
                                                readOnly={!isEditing}
                                                className={`w-full p-4 rounded-2xl text-sm font-medium transition-all ${isEditing ? 'bg-gray-50 border-none focus:ring-2 focus:ring-brand-primary/10' : 'bg-transparent border-transparent cursor-default'}`}
                                                value={formData && maskFullName(user?.fullname)}
                                                onChange={(e) => setFormData({ ...formData, fullname: e.target.value })}
                                                placeholder={t('profile.enter_fullname', 'Enter your full name')}
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        <div>
                                            <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{t('profile.email_label', 'Email Address')}</label>
                                            <input
                                                type="email"
                                                readOnly={true}
                                                className="w-full p-4 rounded-2xl text-sm font-medium bg-transparent border-transparent cursor-default opacity-50"
                                                value={user.email || ''}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{t('profile.phone_label', 'Phone Number')}</label>
                                            <input
                                                type="tel"
                                                readOnly={!isEditing}
                                                className={`w-full p-4 rounded-2xl text-sm font-medium transition-all ${isEditing ? 'bg-gray-50 border-none focus:ring-2 focus:ring-brand-primary/10' : 'bg-transparent border-transparent cursor-default'}`}
                                                value={formData && maskPhone(user?.phone)}
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                placeholder={t('profile.no_phone', 'Not provided')}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {isEditing && (
                                    <div className="mt-12 pt-8 border-t border-gray-50 flex justify-end">
                                        <button
                                            onClick={handleSave}
                                            disabled={saving}
                                            className="bg-brand-primary text-white px-12 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                                        >
                                            {saving ? t('profile.saving', 'Saving...') : t('profile.save_changes', 'Save Changes')}
                                        </button>
                                    </div>
                                )}
                            </div>
                            {/* Danger Zone */}
                            <div className="bg-red-50/50 rounded-3xl border border-red-100 p-10">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-12 h-12 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center text-xl">
                                        <i className="fas fa-exclamation-triangle"></i>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black text-gray-900">{t('profile.danger_zone', 'Danger Zone')}</h3>
                                        <p className="text-xs text-gray-500 font-medium">{t('profile.irreversible_actions', 'Irreversible account actions')}</p>
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6 bg-white rounded-2xl border border-red-100">
                                    <div className="flex-1">
                                        <h4 className="font-black text-gray-900 mb-1">{t('profile.delete_account', 'Delete Account')}</h4>
                                        <p className="text-xs text-gray-400 leading-relaxed max-w-md">
                                            {t('profile.delete_warning', 'Once you request account deletion, your personal data will be anonymized immediately. Your data will be permanently removed after a 90-day legal grace period.')}
                                        </p>
                                    </div>
                                    <button
                                        onClick={async () => {
                                            if (window.confirm(t('profile.confirm_delete', 'Are you sure you want to request account deletion? You will be logged out immediately.'))) {
                                                try {
                                                    const token = getToken()
                                                    const res = await axios.delete(`${CONFIG.API_URL}/api/auth/account`, {
                                                        headers: { Authorization: `Bearer ${token}` }
                                                    })
                                                    alert(res.data.message);
                                                    // Clear local auth and redirect
                                                    localStorage.clear();
                                                    window.location.href = '/';
                                                } catch (err) {
                                                    console.error('Account deletion failed:', err)
                                                    alert(t('profile.delete_error', 'Failed to request account deletion.'))
                                                }
                                            }
                                        }}
                                        className="bg-red-600 text-white px-8 py-4 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-red-200 hover:bg-red-700 hover:scale-[1.02] transition-all whitespace-nowrap"
                                    >
                                        {t('profile.request_deletion', 'Request Deletion')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};


export default UserProfile;

// import React, { useState, useEffect } from 'react';
// import { useTranslation } from 'react-i18next';
// import { getUser, getToken, setUser as setLocalUser } from '../utils/auth';
// import { Link } from 'react-router-dom';
// import axios from 'axios';
// import CONFIG from '../config';
// import { Helmet } from 'react-helmet-async';
// import SEO from '../components/common/SEO';

// const UserProfile = () => {
//     const { t, i18n } = useTranslation();
//     const isArabic = i18n.language.startsWith('ar');
//     const [user, setUser] = useState(getUser());
//     const [isEditing, setIsEditing] = useState(false);
//     const [loading, setLoading] = useState(true);
//     const [saving, setSaving] = useState(false);

//     const [formData, setFormData] = useState({
//         username: '',
//         fullname: '',
//         phone: ''
//     });

//     const fetchProfile = async () => {
//         try {
//             setLoading(true);
//             const token = getToken();
//             const res = await axios.get(`${CONFIG.API_URL}/api/auth/me`, {
//                 headers: { Authorization: `Bearer ${token}` }
//             });
//             setUser(res.data);
//             setLocalUser(res.data); // Keep localStorage in sync
//             setFormData({
//                 username: res.data.username || '',
//                 fullname: res.data.fullname || '',
//                 phone: res.data.phone || ''
//             });
//         } catch (err) {
//             console.error('Failed to fetch profile:', err);
//         } finally {
//             setLoading(false);
//         }
//     };

//     useEffect(() => {
//         fetchProfile();
//     }, []);

//     const handleSave = async () => {
//         try {
//             setSaving(true);
//             const token = getToken();
//             await axios.put(`${CONFIG.API_URL}/api/auth/profile`, formData, {
//                 headers: { Authorization: `Bearer ${token}` }
//             });
//             await fetchProfile();
//             setIsEditing(false);
//             alert(t('profile.update_success', 'Profile updated successfully!'));
//         } catch (err) {
//             console.error('Failed to save profile:', err);
//             alert(t('profile.update_error', 'Failed to update profile.'));
//         } finally {
//             setSaving(false);
//         }
//     };

//     const stats = [
//         { label: t('profile.total_orders', 'Orders'), value: '0', icon: 'fa-box', link: '/profile/orders' },
//         { label: t('profile.wishlist', 'Wishlist'), value: '0', icon: 'fa-heart', link: '/wishlist' },
//         { label: t('profile.cart', 'Cart'), value: '0', icon: 'fa-shopping-cart', link: '/cart' }
//     ];

//     if (loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>;

//     return (
//         <>
//             <Helmet>
//                 <html lang={isArabic ? 'ar' : 'en'} dir={isArabic ? 'rtl' : 'ltr'} />
//             </Helmet>
//             <SEO />
//             <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-20 animate-fade-in">
//                 <div className="flex flex-col lg:flex-row gap-12">
//                     {/* Sidebar */}
//                     <div className="w-full lg:w-80 space-y-4">
//                         <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl p-8 text-center overflow-hidden relative">
//                             <div className="absolute top-0 left-0 w-full h-24 bg-brand-primary/5" />
//                             <div className="relative mt-8">
//                                 <div className="w-24 h-24 mx-auto rounded-full bg-brand-primary text-white flex items-center justify-center text-4xl font-black shadow-xl shadow-brand-primary/20 mb-6 border-4 border-white">
//                                     {user?.username?.charAt(0).toUpperCase() || 'U'}
//                                 </div>
//                                 <h2 className="text-xl font-black text-gray-900 mb-1">{user?.username}</h2>
//                                 <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">{user?.email}</p>
//                             </div>
//                         </div>

//                         <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-4 space-y-1">
//                             <Link to="/profile" className="flex items-center gap-4 p-4 rounded-2xl bg-brand-primary text-white shadow-lg shadow-brand-primary/10 transition-all font-black text-sm">
//                                 <i className="fas fa-user-circle w-6"></i>
//                                 {t('profile.personal_info', 'Personal Info')}
//                             </Link>
//                             <Link to="/profile/orders" className="flex items-center gap-4 p-4 rounded-2xl text-gray-400 hover:bg-gray-50 hover:text-gray-900 transition-all font-black text-sm">
//                                 <i className="fas fa-shopping-bag w-6"></i>
//                                 {t('profile.my_orders', 'My Orders')}
//                             </Link>
//                             <Link to="/wishlist" className="flex items-center gap-4 p-4 rounded-2xl text-gray-400 hover:bg-gray-50 hover:text-gray-900 transition-all font-black text-sm">
//                                 <i className="fas fa-heart w-6"></i>
//                                 {t('profile.wishlist_sidebar', 'Wishlist')}
//                             </Link>
//                         </div>
//                     </div>

//                     {/* Main Content */}
//                     <div className="flex-1 space-y-8">
//                         {/* Stats */}
//                         <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
//                             {stats.map((s, idx) => (
//                                 <Link key={idx} to={s.link} className="bg-white rounded-3xl border border-gray-100 shadow-lg p-6 hover:shadow-2xl hover:-translate-y-1 transition-all group">
//                                     <div className="flex items-center gap-6">
//                                         <div className="w-14 h-14 rounded-2xl bg-gray-50 text-gray-400 flex items-center justify-center text-xl group-hover:bg-brand-primary group-hover:text-white transition-all">
//                                             <i className={`fas ${s.icon}`}></i>
//                                         </div>
//                                         <div>
//                                             <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">{s.label}</p>
//                                             <p className="text-2xl font-black text-gray-900">{s.value}</p>
//                                         </div>
//                                     </div>
//                                 </Link>
//                             ))}
//                         </div>

//                         {/* Content Section */}
//                         <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl p-10">
//                             <div className="flex items-center justify-between mb-10 pb-6 border-b border-gray-50">
//                                 <h3 className="text-2xl font-black text-gray-900 tracking-tight">{t('profile.account_settings', 'Account Settings')}</h3>
//                                 <button
//                                     onClick={() => setIsEditing(!isEditing)}
//                                     className="bg-gray-50 text-gray-400 hover:text-brand-primary hover:bg-brand-primary/5 px-6 py-2.5 rounded-full font-black text-[10px] uppercase tracking-widest transition-all"
//                                 >
//                                     {isEditing ? t('profile.cancel', 'Cancel') : t('profile.edit', 'Edit Profile')}
//                                 </button>
//                             </div>

//                             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
//                                 <div className="space-y-6">
//                                     <div>
//                                         <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{t('profile.username', 'Username')}</label>
//                                         <input
//                                             type="text"
//                                             readOnly={!isEditing}
//                                             className={`w-full p-4 rounded-2xl text-sm font-medium transition-all ${isEditing ? 'bg-gray-50 border-none focus:ring-2 focus:ring-brand-primary/10' : 'bg-transparent border-transparent cursor-default'}`}
//                                             value={formData.username}
//                                             onChange={(e) => setFormData({ ...formData, username: e.target.value })}
//                                         />
//                                     </div>
//                                     <div>
//                                         <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{t('profile.full_name', 'Full Name')}</label>
//                                         <input
//                                             type="text"
//                                             readOnly={!isEditing}
//                                             className={`w-full p-4 rounded-2xl text-sm font-medium transition-all ${isEditing ? 'bg-gray-50 border-none focus:ring-2 focus:ring-brand-primary/10' : 'bg-transparent border-transparent cursor-default'}`}
//                                             value={formData.fullname}
//                                             onChange={(e) => setFormData({ ...formData, fullname: e.target.value })}
//                                             placeholder={t('profile.enter_fullname', 'Enter your full name')}
//                                         />
//                                     </div>
//                                 </div>

//                                 <div className="space-y-6">
//                                     <div>
//                                         <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{t('profile.email_label', 'Email Address')}</label>
//                                         <input
//                                             type="email"
//                                             readOnly={true}
//                                             className="w-full p-4 rounded-2xl text-sm font-medium bg-transparent border-transparent cursor-default opacity-50"
//                                             value={user?.email || ''}
//                                         />
//                                     </div>
//                                     <div>
//                                         <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{t('profile.phone_label', 'Phone Number')}</label>
//                                         <input
//                                             type="tel"
//                                             readOnly={!isEditing}
//                                             className={`w-full p-4 rounded-2xl text-sm font-medium transition-all ${isEditing ? 'bg-gray-50 border-none focus:ring-2 focus:ring-brand-primary/10' : 'bg-transparent border-transparent cursor-default'}`}
//                                             value={formData.phone}
//                                             onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
//                                             placeholder={t('profile.no_phone', 'Not provided')}
//                                         />
//                                     </div>
//                                 </div>
//                             </div>

//                             {isEditing && (
//                                 <div className="mt-12 pt-8 border-t border-gray-50 flex justify-end">
//                                     <button
//                                         onClick={handleSave}
//                                         disabled={saving}
//                                         className="bg-brand-primary text-white px-12 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
//                                     >
//                                         {saving ? t('profile.saving', 'Saving...') : t('profile.save_changes', 'Save Changes')}
//                                     </button>
//                                 </div>
//                             )}
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </>
//     );
// };

// export default UserProfile;






// import React, { useState, useEffect } from 'react'
// import { useTranslation } from 'react-i18next'
// import { getUser, getToken, setUser as setLocalUser } from '../utils/auth'
// import { Link } from 'react-router-dom'
// import axios from 'axios'
// import CONFIG from '../config'
// import { Helmet } from 'react-helmet-async'
// import SEO from '../components/common/SEO'
// import { useCart } from '../context/CartContext'
// import { useWishlist } from '../context/WishlistContext'

// const UserProfile = () => {
//     const { t, i18n } = useTranslation()
//     const isArabic = i18n.language.startsWith('ar')
//     const [user, setUser] = useState(getUser())
//     const [isEditing, setIsEditing] = useState(false)
//     const [loading, setLoading] = useState(true)
//     const [saving, setSaving] = useState(false)
//     const [formData, setFormData] = useState({ username: '', fullname: '', phone: '' })

//     const { cartItems } = useCart()
//     const { wishlistItems } = useWishlist()
//     const [ordersCount, setOrdersCount] = useState(0)

//     const fetchProfile = async () => {
//         try {
//             setLoading(true)
//             const token = getToken()
//             const res = await axios.get(`${CONFIG.API_URL}/api/auth/me`, {
//                 headers: { Authorization: `Bearer ${token}` }
//             })
//             setUser(res.data)
//             setLocalUser(res.data); // Keep localStorage in sync
//             setFormData({
//                 username: res.data.username || '',
//                 fullname: res.data.fullname || '',
//                 phone: res.data.phone || ''
//             })
//         } catch (err) {
//             console.error('Failed to fetch profile:', err)
//         } finally {
//             setLoading(false)
//         }
//     }

//     useEffect(() => {
//         fetchProfile()
//     }, [])

//     useEffect(() => {
//         const fetchOrdersCount = async () => {
//             try {
//                 const token = getToken()
//                 const res = await axios.get(`${CONFIG.API_URL}/api/orders/my-orders`, {
//                     headers: { Authorization: `Bearer ${token}` }
//                 })
//                 setOrdersCount(res.data.length)
//             } catch (err) {
//                 console.error('Failed to fetch orders count:', err)
//             }
//         }
//         fetchOrdersCount()
//     }, [])

//     const handleSave = async () => {
//         try {
//             setSaving(true)
//             const token = getToken()
//             await axios.put(`${CONFIG.API_URL}/api/auth/profile`, formData, {
//                 headers: { Authorization: `Bearer ${token}` }
//             })
//             await fetchProfile()
//             setIsEditing(false)
//             alert(t('profile.update_success', 'Profile updated successfully!'))
//         } catch (err) {
//             console.error('Failed to save profile:', err)
//             alert(t('profile.update_error', 'Failed to update profile.'))
//         } finally {
//             setSaving(false)
//         }
//     }

//     const stats = [
//         {
//             label: t('profile.total_orders', 'Orders'),
//             value: ordersCount,
//             icon: 'fa-box',
//             link: '/profile/orders'
//         },
//         {
//             label: t('profile.wishlist', 'Wishlist'),
//             value: wishlistItems.length,
//             icon: 'fa-heart',
//             link: '/wishlist'
//         },
//         {
//             label: t('profile.cart', 'Cart'),
//             value: cartItems.length,
//             icon: 'fa-shopping-cart',
//             link: '/cart'
//         }
//     ]

//     if (loading) return <div className="flex items-center justify-center min-h-screen">Loading...</div>

//     return (
//         <>
//             <Helmet>
//                 <html lang={isArabic ? 'ar' : 'en'} dir={isArabic ? 'rtl' : 'ltr'} />
//             </Helmet>
//             <SEO />
//             <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-20 animate-fade-in">
//                 <div className="flex flex-col lg:flex-row gap-12">
//                     {/* Sidebar */}
//                     <div className="w-full lg:w-80 space-y-4">
//                         <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl p-8 text-center overflow-hidden relative">
//                             <div className="absolute top-0 left-0 w-full h-24 bg-brand-primary/5" />
//                             <div className="relative mt-8">
//                                 <div className="w-24 h-24 mx-auto rounded-full bg-brand-primary text-white flex items-center justify-center text-4xl font-black shadow-xl shadow-brand-primary/20 mb-6 border-4 border-white">
//                                     {user?.username?.charAt(0).toUpperCase() || 'U'}
//                                 </div>
//                                 <h2 className="text-xl font-black text-gray-900 mb-1">{user?.username}</h2>
//                                 <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">{user?.email}</p>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </>
//     )
// }

// export default UserProfile
